<template>
  <div class="container">
    <h2 id="landcover">2. Land Cover</h2>
    <div class="row">
      <div class="col">
        <p>
          Land cover data is collected from the ESA Climate Change Initiative (CCI) Land Cover dataset [<a href="#ref-2">2</a>]. The ESA-CCI dataset contains annual land cover maps at 300m resolution for years from 1992 to 2020. The distribution of land cover classes in the study area in 2020 are shown in the table below, as well as the total area and the percentage difference in 2020 compared to the earliest data from 1992. 
        </p>
      </div>
    </div>
    <div class="row justify-content-md-center">
      <div class="col-lg-12 col-md-12 col-sm-12">
        <table class='table table-sm' style="font-size:0.8em;margin-top:10px;" v-if="loaded">
          <thead>
            <tr><th colspan="6" style="padding-left:0px;border-top:0px;">Table 1. Land Cover Classification (2020)</th></tr>
            <tr>
                <th style="padding-left:0px;border:0px;">Coverage [%]</th>
                <th style="border:0px;"></th>
                <th style="border:0px;" colspan="2">Classification</th>
                <th style="border:0px;text-align:center;"><nobr>Area [km<sup>2</sup>]</nobr></th>
                <th style="border:0px;"><nobr>Change [%]</nobr></th>
            </tr>
          </thead>
          <tbody>
            <landcover-item v-for="item in landCoverClassList" v-bind:key="item.key" v-bind:item="item" v-bind:year="2020" />
          </tbody>
          <tfoot>
            <tr><td colspan="6" style="font-size:0.8em;padding:0px;color:#888;">                    
                Land cover classes with less than 0.5% coverage are not listed. Source: ESA Climate Change Initiative. [<a href="#ref-2">2</a>]
            </td></tr>
          </tfoot>
        </table>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <p>
          The actual land cover classification maps are shown in the map viewer below. Different epochs can be selected using the layer selection tabs.
        </p>
      </div>
    </div>
    <div class="row justify-content-md-center">
      <div class="col-lg-12">
        <RasterMap 
          backgrounds="streets,satellite,light,osm" 
          title="Land Cover Classification" 
          attribution="Map: Geofolio. Data: ESA Climate Change Initiative [2]. Backgrounds: Mapbox/Openstreetmap Contributors [13]." 
          v-bind:layout="{legendWidth:8, legendCols:2, descriptionWidth:4 }" 
          v-bind:location="location"
          v-bind:factsheet="factsheet"
          v-bind:loaded="loaded"
          v-bind:layers="layers">
        </RasterMap>
      </div>
    </div>

  </div>
</template>


<script>
import _ from 'lodash';
import Vue from 'vue';
import RasterMap from '@/components/factsheet/RasterMap.vue'

const LandcoverItem = Vue.component('landcover-item', {
    props: ['item', 'year'],
    template: `
        <tr>
          <td style="padding-left:0px;">
            <span style="background-color:#eee;width:200px;;display:inline-block;">
              <span v-bind:style="style">&nbsp;</span>
            </span>
          </td>
          <td style="text-align:center;">
          <span style="padding-left:5px;">{{percent.toFixed(1)}}%</span>
          </td>
          <td>
            <span v-bind:style="colorStyle">&nbsp;</span>
          </td>
          <td style="width:100%;">
            {{ item.title }}
          </td>
          <td style="text-align:center;">{{area.toFixed(1)}}</td>
          <td style="text-align:center;">
            <span v-if="percentDifference > 0" style="border-radius:3px;background-color:#f5f5f5;font-size:0.8em;color:#15b01a;padding:3px;"><nobr><i class="fas fa-caret-up" style="font-size:0.8em;"></i> {{percentDifference.toFixed(1)}}%</nobr></span>
            <span v-else style="border-radius:3px;background-color:#f5f5f5;color:#e50000;font-size:0.8em;padding:3px;"><nobr><i class="fas fa-caret-down"  style="font-size:0.8em;"></i> {{(percentDifference*-1).toFixed(1)}}%</nobr></span>
          </td>
        </tr>
    `,
    computed: {
        style: function() {
          return 'display:inline-block;background-color:#007bff;min-width:5px;width:'+this.percent+'%';
        },
        colorStyle: function() {
          return 'display:inline-block;background-color:'+this.item.color+';width:50px;'
        },
        percent: function() {
          return parseFloat(this.item.percent[parseInt(this.year)-1992])
        },
        percentDifference: function() {
          return parseFloat(this.item.percent[parseInt(this.year)-1992] - this.item.percent[0])
        },
        area: function() {
          return parseFloat(this.item.count[parseInt(this.year)-1992] * 0.09 )
        }
    }
});

export default {
    name: 'Landcover',
    components:{
        LandcoverItem, RasterMap
    },
    props: ['location', 'factsheet', 'loaded'],
    data() {
        return {
          'layers': [
            {'title': '2020', 'source': '/esa_cci_landcover/esa_cci_landcover_2020.tif', long_description: "<p>Land cover classification data from ESA Climate Change Initiative. The most recent dataset from 2020 is shown by default in the map, and other years can be selected for detailed comparisons. Classes which are not present in the area of interest are not shown in the legend. Dataset is available at 300m spatial resolution, and created from MERIS, AVHRR, and PROVA-V satellite imagery.</p><p>The source data for a particular year can be downloaded in GeoTIFF format using the download button.</p>"},
            {'title': '2015', 'source': '/esa_cci_landcover/esa_cci_landcover_2015.tif', long_description: "<p>Land cover classification data from ESA Climate Change Initiative. The most recent dataset from 2020 is shown by default in the map, and other years can be selected for detailed comparisons. Classes which are not present in the area of interest are not shown in the legend. Dataset is available at 300m spatial resolution, and created from MERIS, AVHRR, and PROVA-V satellite imagery.</p><p>The source data for a particular year can be downloaded in GeoTIFF format using the download button.</p>"},
            {'title': '2010', 'source': '/esa_cci_landcover/esa_cci_landcover_2010.tif', long_description: "<p>Land cover classification data from ESA Climate Change Initiative. The most recent dataset from 2020 is shown by default in the map, and other years can be selected for detailed comparisons. Classes which are not present in the area of interest are not shown in the legend. Dataset is available at 300m spatial resolution, and created from MERIS, AVHRR, and PROVA-V satellite imagery.</p><p>The source data for a particular year can be downloaded in GeoTIFF format using the download button.</p>"},
            {'title': '2005', 'source': '/esa_cci_landcover/esa_cci_landcover_2005.tif', long_description: "<p>Land cover classification data from ESA Climate Change Initiative. The most recent dataset from 2020 is shown by default in the map, and other years can be selected for detailed comparisons. Classes which are not present in the area of interest are not shown in the legend. Dataset is available at 300m spatial resolution, and created from MERIS, AVHRR, and PROVA-V satellite imagery.</p><p>The source data for a particular year can be downloaded in GeoTIFF format using the download button.</p>"},
            {'title': '2000', 'source': '/esa_cci_landcover/esa_cci_landcover_2000.tif', long_description: "<p>Land cover classification data from ESA Climate Change Initiative. The most recent dataset from 2020 is shown by default in the map, and other years can be selected for detailed comparisons. Classes which are not present in the area of interest are not shown in the legend. Dataset is available at 300m spatial resolution, and created from MERIS, AVHRR, and PROVA-V satellite imagery.</p><p>The source data for a particular year can be downloaded in GeoTIFF format using the download button.</p>"},
            {'title': '1995', 'source': '/esa_cci_landcover/esa_cci_landcover_1995.tif', long_description: "<p>Land cover classification data from ESA Climate Change Initiative. The most recent dataset from 2020 is shown by default in the map, and other years can be selected for detailed comparisons. Classes which are not present in the area of interest are not shown in the legend. Dataset is available at 300m spatial resolution, and created from MERIS, AVHRR, and PROVA-V satellite imagery.</p><p>The source data for a particular year can be downloaded in GeoTIFF format using the download button.</p>"},
            {'title': '1992', 'source': '/esa_cci_landcover/esa_cci_landcover_1992.tif', long_description: "<p>Land cover classification data from ESA Climate Change Initiative. The most recent dataset from 2020 is shown by default in the map, and other years can be selected for detailed comparisons. Classes which are not present in the area of interest are not shown in the legend. Dataset is available at 300m spatial resolution, and created from MERIS, AVHRR, and PROVA-V satellite imagery.</p><p>The source data for a particular year can be downloaded in GeoTIFF format using the download button.</p>"}
          ]
        }
    },
    created() {
    },
    computed: {
        landCoverClassList: function() {
            var filtered = _.filter(this.factsheet.data.esa_cci_landcover.classification, ({percent}) => percent[percent.length-1] > 0.5)
            return _.orderBy(filtered, function(p) { return p.percent[p.percent.length-1] }, ['desc'])
        }
    },
    mounted() {
    }
}
</script>

<style>

</style>